import { useEffect, useRef, useState } from 'react';

import { Icon } from './Icon';
import { CODEC_LABEL } from '../lib/codec';
import {
  AUDIO_MODE_LABEL,
  AUDIO_MODE_NOTE,
  formatAudioBitrate,
  type AudioMode,
} from '../lib/audio';
import {
  VIDEO_MODE_LABEL,
  VIDEO_MODE_NOTE,
  type VideoMode,
} from '../lib/video';
import type { AudioStatus, CodecStatus, VideoStatus } from '../lib/useRoomConnection';
import './QualityPanel.css';

/**
 * Качество встречи: что происходит со связью и чем можно управлять
 * прямо во время разговора.
 */
export function QualityPanel({
  codec,
  video,
  audio,
  onVideoMode,
  onAudioMode,
  onNoiseSuppression,
  onOpenChange,
}: {
  codec: CodecStatus;
  video: VideoStatus;
  audio: AudioStatus;
  onVideoMode: (mode: VideoMode) => void;
  onAudioMode: (mode: AudioMode) => void;
  onNoiseSuppression: (enabled: boolean) => void;
  /** Сообщает наверх, нужно ли вообще считать статистику. */
  onOpenChange: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onOpenChange(open);
  }, [open, onOpenChange]);

  // Клик мимо и Escape закрывают панель.
  useEffect(() => {
    if (!open) return;

    const onPointer = (event: PointerEvent) => {
      if (!hostRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const sample = codec.sample;

  return (
    <div className="quality" ref={hostRef}>
      <button
        type="button"
        className={`quality__button ${open ? 'quality__button--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="Качество связи"
      >
        <Icon name="waves" size={16} />
        <span className="quality__codec">{CODEC_LABEL[codec.active]}</span>
        {codec.auto && <span className="quality__auto">авто</span>}
      </button>

      {open && (
        <div className="quality__pop" role="dialog" aria-label="Качество связи">
          <section className="quality__section">
            <h3 className="quality__heading">Связь</h3>
            {sample ? (
              <dl className="quality__stats">
                <dt>Отправка</dt>
                <dd>
                  {sample.sending
                    ? `${sample.width}×${sample.height}${sample.fps ? ` · ${Math.round(sample.fps)}/с` : ''}`
                    : 'на паузе'}
                </dd>
                <dt>Битрейт видео</dt>
                <dd>
                  {!sample.sending
                    ? '—'
                    : sample.measured
                      ? `${(sample.sendBitrate / 1e6).toFixed(2)} Мбит/с`
                      : 'измеряем…'}
                </dd>
                <dt>Полоса</dt>
                <dd>{sample.bitrate ? `${(sample.bitrate / 1e6).toFixed(1)} Мбит/с` : 'н/д'}</dd>
                <dt>Потери</dt>
                <dd>{(sample.loss * 100).toFixed(1)}%</dd>
                <dt>Задержка</dt>
                <dd>{Math.round(sample.rtt * 1000)} мс</dd>
                <dt>Ограничение</dt>
                <dd>{LIMIT_LABEL[sample.limitation]}</dd>
              </dl>
            ) : (
              <p className="quality__empty">Статистика появится, когда включена камера.</p>
            )}
            {sample && !sample.sending && (
              <p className="quality__note">
                Пока вас никто не смотрит, видео не передаётся — канал не тратится впустую.
              </p>
            )}
          </section>

          <section className="quality__section">
            <h3 className="quality__heading">Видео</h3>

            <div className="quality__modes" role="group" aria-label="Качество видео">
              {(['auto', 'high', 'saver'] as VideoMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={`quality__mode ${video.mode === mode ? 'quality__mode--on' : ''}`}
                  onClick={() => onVideoMode(mode)}
                  aria-pressed={video.mode === mode}
                >
                  {VIDEO_MODE_LABEL[mode]}
                </button>
              ))}
            </div>

            <p className="quality__note">{VIDEO_MODE_NOTE[video.mode]}</p>
            <p className="quality__line">
              Кодек: <b>{CODEC_LABEL[codec.active]}</b>
              {codec.auto ? ' · подобран автоматически' : ' · выбран вручную'}
            </p>
          </section>

          <section className="quality__section">
            <h3 className="quality__heading">Звук</h3>

            <div className="quality__modes" role="group" aria-label="Режим звука">
              {(['auto', 'speech'] as AudioMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={`quality__mode ${audio.mode === mode ? 'quality__mode--on' : ''}`}
                  onClick={() => onAudioMode(mode)}
                  aria-pressed={audio.mode === mode}
                >
                  {AUDIO_MODE_LABEL[mode]}
                </button>
              ))}
            </div>

            <p className="quality__note">{AUDIO_MODE_NOTE[audio.mode]}</p>
            <p className="quality__line">
              Битрейт: <b>{formatAudioBitrate(audio.bitrate)}</b>
            </p>
            {audio.lastChange && <p className="quality__change">{audio.lastChange}</p>}

            <label className="switch quality__switch">
              <input
                type="checkbox"
                checked={audio.noiseSuppression}
                onChange={(e) => onNoiseSuppression(e.target.checked)}
              />
              <span className="switch__track" aria-hidden>
                <span className="switch__knob" />
              </span>
              <span className="switch__text">
                <span className="switch__title">Шумоподавление</span>
                <span className="switch__note">Штатный фильтр браузера</span>
              </span>
            </label>
          </section>

          {/*
            На телефоне панель занимает почти весь экран, и промахнуться
            мимо неё, чтобы закрыть, почти негде. Явная кнопка внизу —
            там, где до неё дотягивается большой палец.
          */}
          <button type="button" className="quality__close" onClick={() => setOpen(false)}>
            <Icon name="close" size={16} />
            Закрыть
          </button>
        </div>
      )}
    </div>
  );
}

const LIMIT_LABEL: Record<string, string> = {
  none: 'нет',
  cpu: 'процессор',
  bandwidth: 'канал',
  other: 'прочее',
};
